import { defineComponent, ref, computed, isVue3 } from 'vue-demi';
import { s as script$1, a as IconSize } from './OrIcon-9aff520e.js';
import { s as script$2 } from './OrTooltip-de856939.js';
import { resolveComponent, openBlock, createBlock, normalizeProps, guardReactiveProps, createSlots, withCtx, createElementVNode, mergeProps, toHandlers, createVNode, renderSlot } from 'vue';

var IconButtonColor;
(function (IconButtonColor) {
  IconButtonColor["Default"] = "default";
  IconButtonColor["Primary"] = "primary";
  IconButtonColor["Danger"] = "danger";
  IconButtonColor["Inherit"] = "inherit";
})(IconButtonColor || (IconButtonColor = {}));

const IconButton = [
// Layout
'layout-inline-row justify-center',
// Shape
'rounded-full',
// Interactivity
'interactivity-click',
// Typography
'typography-button',
// Iconography
'iconography-outlined',
// Iconography (hover)
'hover:md:iconography-filled',
// Iconography (focus)
'focus-visible:md:iconography-filled',
// Iconography (active)
'active:iconography-filled',
// Theme
'!outline outline-2 outline-transparent'];
const IconButtonColors = {
  [IconButtonColor.Default]: [
  // Theme
  'theme-preset-3-primary', 'dark:theme-preset-3-primary-dark', 'theme-foreground-outline', 'dark:theme-foreground-outline-dark',
  // Theme (hover)
  'hover:md:theme-foreground-default', 'dark:hover:md:theme-foreground-default-dark', 'hover:md:outline-primary-opacity-0-08', 'dark:hover:md:outline-primary-opacity-0-08-dark',
  // Theme (focus)
  'focus-visible:md:theme-foreground-default', 'dark:focus-visible:md:theme-foreground-default-dark', 'focus-visible:md:outline-primary-opacity-0-08', 'dark:focus-visible:md:outline-primary-opacity-0-08-dark',
  // Theme (active)
  'active:theme-foreground-default', 'dark:active:theme-foreground-default-dark', 'active:outline-primary-opacity-0-08', 'dark:active:outline-primary-opacity-0-08-dark', 'active:md:outline-primary-opacity-0-16', 'dark:active:md:outline-primary-opacity-0-16-dark',
  // Theme (selected)
  'selected:theme-foreground-default', 'dark:selected:theme-foreground-default-dark'],
  [IconButtonColor.Primary]: [
  // Theme
  'theme-preset-3-primary', 'dark:theme-preset-3-primary-dark',
  // Theme (hover)
  'hover:md:outline-primary-opacity-0-08', 'dark:hover:md:outline-primary-opacity-0-08-dark',
  // Theme (focus)
  'focus-visible:md:outline-primary-opacity-0-08', 'dark:focus-visible:md:outline-primary-opacity-0-08-dark',
  // Theme (active)
  'active:outline-primary-opacity-0-08', 'dark:active:outline-primary-opacity-0-08-dark', 'active:md:outline-primary-opacity-0-16', 'dark:active:md:outline-primary-opacity-0-16-dark'],
  [IconButtonColor.Danger]: [
  // Theme
  'theme-preset-3-error', 'dark:theme-preset-3-error-dark',
  // Theme (hover)
  'hover:md:outline-error-opacity-0-08', 'dark:hover:md:outline-error-opacity-0-08-dark',
  // Theme (focus)
  'focus-visible:md:outline-error-opacity-0-08', 'dark:focus-visible:md:outline-error-opacity-0-08-dark',
  // Theme (active)
  'active:outline-error-opacity-0-08', 'dark:active:outline-error-opacity-0-08-dark', 'active:md:outline-error-opacity-0-16', 'dark:active:md:outline-error-opacity-0-16-dark'],
  [IconButtonColor.Inherit]: [
  // Theme
  'theme-preset-3-primary', 'dark:theme-preset-3-primary-dark', 'theme-foreground-inherit', 'dark:theme-foreground-inherit-dark',
  // Theme (hover)
  'hover:md:outline-primary-opacity-0-08', 'dark:hover:md:outline-primary-opacity-0-08-dark',
  // Theme (focus)
  'focus-visible:md:outline-primary-opacity-0-08', 'dark:focus-visible:md:outline-primary-opacity-0-08-dark',
  // Theme (active)
  'active:outline-primary-opacity-0-08', 'dark:active:outline-primary-opacity-0-08-dark', 'active:md:outline-primary-opacity-0-16', 'dark:active:md:outline-primary-opacity-0-16-dark']
};

var script = defineComponent({
  name: 'OrIconButton',
  components: {
    OrIcon: script$1,
    OrTooltip: script$2
  },
  inheritAttrs: false,
  props: {
    // TODO: Remove after migration to Vue 3
    additionalStyles: {
      type: Array,
      default: () => []
    },
    icon: {
      type: [String, Object],
      required: true
    },
    color: {
      type: String,
      default: () => IconButtonColor.Default
    },
    size: {
      type: String,
      default: () => IconSize.M
    },
    selected: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    tooltip: {
      type: [String, Object],
      default: undefined
    }
  },
  emits: ['click', 'focus', 'blur'],
  expose: ['root'],
  setup(props) {
    // Refs
    const root = ref();
    // Styles
    const rootStyles = computed(() => ['or-icon-button-v3', ...IconButton, ...IconButtonColors[props.color]]);
    return {
      root,
      rootStyles,
      isVue3
    };
  }
});

const _hoisted_1 = ["selected", "disabled"];
function render(_ctx, _cache, $props, $setup, $data, $options) {
  const _component_OrIcon = resolveComponent("OrIcon");
  const _component_OrTooltip = resolveComponent("OrTooltip");
  return openBlock(), createBlock(_component_OrTooltip, normalizeProps(guardReactiveProps(typeof _ctx.tooltip === 'string' ? {
    content: _ctx.tooltip
  } : _ctx.tooltip)), createSlots({
    default: withCtx(() => [createElementVNode("button", mergeProps(_ctx.$attrs, {
      ref: 'root',
      class: [_ctx.rootStyles, ..._ctx.additionalStyles],
      type: 'button',
      selected: _ctx.selected,
      disabled: _ctx.disabled
    }, toHandlers({
      ...(_ctx.isVue3 ? {} : _ctx.$listeners),
      click: e => _ctx.$emit('click', e),
      focus: e => _ctx.$emit('focus', e),
      blur: e => _ctx.$emit('blur', e)
    }, true)), [createVNode(_component_OrIcon, normalizeProps(guardReactiveProps(typeof _ctx.icon === 'string' ? {
      icon: _ctx.icon,
      variant: 'inherit',
      size: _ctx.size
    } : {
      variant: 'inherit',
      size: _ctx.size,
      ..._ctx.icon
    })), null, 16 /* FULL_PROPS */)], 16 /* FULL_PROPS */, _hoisted_1)]),
    _: 2 /* DYNAMIC */
  }, [_ctx.$slots.tooltipContent ? {
    name: "content",
    fn: withCtx(() => [renderSlot(_ctx.$slots, "tooltipContent")]),
    key: "0"
  } : undefined]), 1040 /* FULL_PROPS, DYNAMIC_SLOTS */);
}

script.render = render;
script.__file = "src/components/or-icon-button-v3/OrIconButton.vue";

export { IconButtonColor as I, script as s };
